import { ReactNode, useMemo } from "react";
import { Control, Controller, FieldError, FieldPath, FieldValues, Merge } from "react-hook-form";
import { FormattedMessage, useIntl } from "react-intl";
import Select, { components, GroupBase, InputProps } from "react-select";

import Error from "@components/fields/Error";
import RequiredFieldIndicator from "@components/fields/RequiredFieldIndicator";
import styles from "@components/fields/Select.module.css";
import Nbsp from "@components/Nbsp";
import InputLabel from "@components/typography/InputLabel";
import InputSubLabel from "@components/typography/InputSubLabel";
import { countrySelectOptions } from "@utils/countryUtils";
import { currencySelectOptions } from "@utils/currencyUtils";
import { floatingSelectStyles, selectStyles } from "@utils/formUtils";
import { languageSelectOptions } from "@utils/languageUtils";
import { timezoneSelectOptions } from "@utils/timeUtils";

type DataTypes = "countries" | "currencies" | "languages" | "timezones";

type Props<FormData extends FieldValues> = {
  control: Control<FormData>;
  disabled?: boolean;
  error: Merge<FieldError, (FieldError | undefined)[]> | undefined;
  global?: boolean;
  hasNoneOption?: boolean;
  isMulti?: boolean;
  label?: string | ReactNode;
  labelPlacement?: "stacked";
  name: FieldPath<FormData>;
  onSelection?: (code: string) => void;
  required?: boolean;
  simpleInput?: boolean;
  smallerLabel?: boolean;
  subLabel?: string | ReactNode;
  type: DataTypes;
};

type Option = {
  color?: string | undefined;
  label: string;
  value: string;
};

const Input = ({ ...props }) => (
  <components.Input {...(props as InputProps<unknown, boolean, GroupBase<unknown>>)} data-lpignore="true" />
);

const DataSelect = <FormData extends FieldValues>({
  control,
  disabled,
  error,
  global = false,
  hasNoneOption,
  isMulti = false,
  label,
  labelPlacement,
  name,
  onSelection,
  required,
  simpleInput = true,
  subLabel,
  type
}: Props<FormData>) => {
  const intl = useIntl();

  const selectOptions = useMemo(() => {
    let suggestedOptionValues: string[] = [];
    if (type === "countries") {
      if (global) {
        suggestedOptionValues = ["GLOBAL", "US"];
      } else {
        suggestedOptionValues = ["US"];
      }
    }

    let options: Option[] = [];
    if (type === "countries") {
      options = countrySelectOptions(isMulti);
    } else if (type === "currencies") {
      options = currencySelectOptions(isMulti);
    } else if (type === "languages") {
      options = languageSelectOptions(isMulti);
    } else {
      options = timezoneSelectOptions(isMulti);
    }

    let result: Option[] = [];
    result = (
      suggestedOptionValues.map(value => {
        const matchingOption = options.find(opt => opt.value === value) as Option;
        return {
          color: isMulti ? "#6ab7d0" : undefined,
          label: matchingOption.label,
          value
        };
      }) as Option[]
    ).concat(options.filter(option => !suggestedOptionValues.includes(option.value)));

    if (hasNoneOption) {
      result = [{ label: intl.formatMessage({ id: "dictionary.none" }), value: "" } as Option].concat(result);
    }

    return result;
  }, [global, hasNoneOption, intl, isMulti, type]);

  const getValue = (value: unknown) => {
    if (isMulti) {
      return selectOptions.filter(option => (value as string[]).includes(option.value));
    }
    return (
      selectOptions.find(option => option.value === value) ??
      selectOptions.find(option => option.value === "") ?? { label: "", value: "" }
    );
  };

  const arrayErrors = useMemo(() => {
    if (error === undefined) {
      return undefined;
    }

    if (!Array.isArray(error)) {
      return [error];
    }
    return error;
  }, [error]);

  return (
    <div className={`${styles.wrapperItem}`}>
      {label && (
        <div>
          <InputLabel labelPlacement={labelPlacement}>{label}</InputLabel>
          {subLabel && (
            <>
              <Nbsp />
              <InputSubLabel>
                <FormattedMessage id="dictionary.punctuation.openParen" />
                {subLabel}
                <FormattedMessage id="dictionary.punctuation.closeParen" />
              </InputSubLabel>
            </>
          )}
          {required && <InputLabel>{required && <RequiredFieldIndicator />}</InputLabel>}
        </div>
      )}
      <Controller
        control={control}
        name={name}
        render={({ field: { name, onChange, value } }) => (
          <div className={styles.margin}>
            <Select
              aria-describedby={error ? `${name}-error` : ""}
              aria-invalid={error?.message?.length ? "true" : "false"}
              aria-label={name}
              aria-required={required}
              classNamePrefix="react-select-data"
              closeMenuOnSelect={!isMulti}
              components={{ Input }}
              id={`${name}-input`}
              isDisabled={disabled}
              isMulti={isMulti ? isMulti : undefined}
              isSearchable
              menuPortalTarget={document.body}
              name={name}
              onChange={option => {
                const properlyTypedOption = option as unknown as Option;
                const properlyTypedMultiOption = option as unknown as Option[];
                if (isMulti) {
                  onChange(properlyTypedMultiOption.map(e => e.value));
                } else if (properlyTypedOption.value === "") {
                  onChange(null);
                } else {
                  onChange(properlyTypedOption.value);
                  onSelection?.(properlyTypedOption.value);
                }
              }}
              openMenuOnFocus
              options={selectOptions}
              placeholder={intl.formatMessage({
                id: type === "timezones" ? "dictionary.selectTimezone" : "dictionary.selectBestMatch"
              })}
              styles={simpleInput ? selectStyles() : floatingSelectStyles()}
              value={getValue(value)}
            />
          </div>
        )}
      />

      {arrayErrors && arrayErrors.length > 0 && (
        <div id={`${name}-errors`}>{arrayErrors?.map(error => <Error error={error} key={error?.message} />)}</div>
      )}
    </div>
  );
};

export default DataSelect;
